/**
 * Reflections API Service
 *
 * API client functions for reflection management including CRUD operations
 * and multipart upload support for large audio files.
 */

import { apiClient } from "./api";
import type {
  ReflectionResponse,
  ReflectionWithRecordingResponse,
  MultipartUploadSession,
  CompletedPart,
  CompleteResponse,
  ReflectionContentType,
  ProcessingStatusResponse,
} from "@/types/reflection";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Create a new reflection without file
 */
export async function createReflection(data: {
  content_type?: ReflectionContentType;
  title?: string;
  description?: string;
}): Promise<ReflectionResponse> {
  const response = await apiClient.post(`${API_PREFIX}/reflections`, data);
  return response.data;
}

/**
 * Create a new journal (chat-based) reflection
 *
 * Journal reflections are text-based and start immediately in completed status
 * with a chat session ready for conversation. No audio upload required.
 */
export async function createJournalReflection(options: {
  title?: string;
  description?: string;
  userContext?: string;
  therapyStyleId?: string;
}): Promise<ReflectionResponse> {
  const response = await apiClient.post(`${API_PREFIX}/reflections`, {
    content_type: "journal",
    title: options.title,
    description: options.description,
    user_context: options.userContext,
    therapy_style_id: options.therapyStyleId,
  });
  return response.data;
}

/**
 * List current user's reflections
 */
export async function listReflections(
  limit = 50,
  offset = 0
): Promise<ReflectionResponse[]> {
  const response = await apiClient.get(`${API_PREFIX}/reflections`, {
    params: { limit, offset },
  });
  return response.data;
}

/**
 * Get a specific reflection by ID
 */
export async function getReflection(id: string): Promise<ReflectionResponse> {
  const response = await apiClient.get(`${API_PREFIX}/reflections/${id}`);
  return response.data;
}

/**
 * Update a reflection
 */
export async function updateReflection(
  id: string,
  data: {
    title?: string;
    description?: string;
    status?: string;
  }
): Promise<ReflectionResponse> {
  const response = await apiClient.patch(
    `${API_PREFIX}/reflections/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete a reflection
 */
export async function deleteReflection(id: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/reflections/${id}`);
}

/**
 * Initiate multipart upload for large audio files
 */
export async function initiateMultipartUpload(
  filename: string,
  fileSize: number,
  mimeType: string,
  options?: {
    title?: string;
    description?: string;
    userContext?: string;
    chunkSize?: number;
    therapyStyleId?: string;
    expectedSpeakers?: Record<string, unknown>;
    conversationPartnerId?: string | null;
    isWithPrimaryPartner?: boolean;
  }
): Promise<MultipartUploadSession> {
  const response = await apiClient.post(
    `${API_PREFIX}/reflections/upload/initiate`,
    {
      filename,
      file_size: fileSize,
      mime_type: mimeType,
      title: options?.title,
      description: options?.description,
      user_context: options?.userContext,
      chunk_size: options?.chunkSize,
      therapy_style_id: options?.therapyStyleId,
      expected_speakers: options?.expectedSpeakers,
      conversation_partner_id: options?.conversationPartnerId,
      is_with_primary_partner: options?.isWithPrimaryPartner,
    }
  );
  return response.data;
}

/**
 * Complete multipart upload after all parts are uploaded
 */
export async function completeMultipartUpload(
  reflectionId: string,
  parts: CompletedPart[]
): Promise<CompleteResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/reflections/upload/${reflectionId}/complete`,
    { parts }
  );
  return response.data;
}

/**
 * Abort/cancel an in-progress multipart upload
 */
export async function abortMultipartUpload(
  reflectionId: string
): Promise<void> {
  await apiClient.delete(
    `${API_PREFIX}/reflections/upload/${reflectionId}/abort`
  );
}

/**
 * Get processing status for a recording
 *
 * Returns the current transcription progress including stage,
 * percentage complete, and estimated time remaining.
 */
export async function getProcessingStatus(
  recordingId: string
): Promise<ProcessingStatusResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/${recordingId}/processing`
  );
  return response.data;
}

/**
 * Get reflection with recording details
 */
export async function getReflectionWithRecording(
  reflectionId: string
): Promise<ReflectionWithRecordingResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/reflections/${reflectionId}/details`
  );
  return response.data;
}

/**
 * Retry a failed pipeline stage for a reflection.
 *
 * Re-queues the appropriate processing task. Only works when the
 * pipeline is in a FAILED state with a retryable error.
 */
export async function retryPipelineStage(
  reflectionId: string
): Promise<{ success: boolean; message: string; stage: string }> {
  const response = await apiClient.post(
    `${API_PREFIX}/pipeline/${reflectionId}/retry`
  );
  return response.data;
}

/**
 * Skip a failed non-critical pipeline stage.
 *
 * Only the insights stage can be skipped. Marks the stage as
 * skipped and completes the pipeline without insights.
 */
export async function skipPipelineStage(
  reflectionId: string
): Promise<{ success: boolean; message: string; stage: string }> {
  const response = await apiClient.post(
    `${API_PREFIX}/pipeline/${reflectionId}/skip-stage`
  );
  return response.data;
}
